import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as JspUtil from '../common/JspUtil';
import * as actions from './actionCreators';
import ReCAPTCHA from 'react-google-recaptcha';
import { useJspVariables, useAppContext, useLoader, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer, PageHeader, TextInput, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const PageRegisterGlobalIdWithCompany = () => {
    useDebugRender('PageRegisterGlobalIdWithCompany');
    usePageTitle('Register Global ID');
    const location = useLocation();
    const navigate = useNavigate();
    const { panelStyle, launcher, jspVariables } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { showLoader, hideLoader } = useLoader();

    const query = location && location.query ? location.query : {};
    const [email, setEmail] = useState(query.email || '');
    const [firstName, setFirstName] = useState(query.firstName || '');
    const [lastName, setLastName] = useState(query.lastName || '');
    const [loginApp, setLoginApp] = useState('');
    const [externalCompanyId, setExternalCompanyId] = useState(query.externalCompanyId || '');
    const [companyDesc, setCompanyDesc] = useState(query.companyDesc || '');
    const [captchaResponse, setCaptchaResponse] = useState('');
    const [alert, setAlert] = useState(null);
    const [successDialog, setSuccessDialog] = useState(null);
    const [errors, setErrors] = useState({});
    const ignoreCaptcha = jspVariables.jspDisableCaptcha === 'Y';

    const validate = () => {
        const fieldErrors = {};
        if (!email) fieldErrors.email = errorCodes.MSG_EMAIL_REQUIRED();
        if (!firstName) fieldErrors.firstName = errorCodes.MSG_FIRST_NAME_REQUIRED();
        if (!lastName) fieldErrors.lastName = errorCodes.MSG_LAST_NAME_REQUIRED();
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    useEffect(() => {
        if (JspUtil.isValueAvailable(window.jspGlobalId))
            setEmail(window.jspGlobalId);
        if (JspUtil.isValueAvailable(window.jspFirstName))
            setFirstName(window.jspFirstName);
        if (JspUtil.isValueAvailable(window.jspLastName))
            setLastName(window.jspLastName);
        if (JspUtil.isValueAvailable(window.jspLoginApp))
            setLoginApp(window.jspLoginApp);
        if (JspUtil.isValueAvailable(window.jspExternalCompanyId))
            setExternalCompanyId(window.jspExternalCompanyId);
        if (JspUtil.isValueAvailable(window.jspCompanyDesc))
            setCompanyDesc(window.jspCompanyDesc);
    }, []);

    const onFormSubmit = async (e) => {
        e.preventDefault();
        setAlert(null);
        if (!validate()) return;
        showLoader('Registering Global ID...');
        try {
            await actions.registerGlobalIdWithCompany(email.trim(), firstName, lastName,
                loginApp, externalCompanyId, captchaResponse);
            setSuccessDialog({
                heading: getLabelFromJspVariables('title_request_globalid'),
                message: JspUtil.getLabelFromJspVariables('confirmation_code_sent_email', email)
            });
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    return (
        <PageContainer id="pageRegisterGlobalIdWithCompany">
            <MuiDialog open={!!successDialog} maxWidth="sm" fullWidth
                aria-labelledby="register-global-id-success-title"
                aria-describedby="register-global-id-success-description">
                <DialogTitleWithClose id="register-global-id-success-title" severity="success" onClose={() => navigate(constants.ROUTE_PAGE_LOGIN)} dataAutomationId="register_global_id_company_success_title">
                    {successDialog?.heading}
                </DialogTitleWithClose>
                <DialogContent id="register-global-id-success-description">{successDialog?.message}</DialogContent>
                <DialogActions>
                    <Button variant="contained"
                        onClick={() => navigate(constants.ROUTE_PAGE_LOGIN)}
                        data-automation-id="register_global_id_company_success_login_btn">
                        {getLabelFromJspVariables('action_login')}
                    </Button>
                </DialogActions>
            </MuiDialog>
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="register_global_id_company_header">{getLabelFromJspVariables('title_request_globalid')}</PageHeader>
                {alert && <Alert id="pageRegisterGlobalIdWithCompanyAlert" data-automation-id="page_register_global_id_with_company_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Box component="form" onSubmit={onFormSubmit} id="formRegisterGlobalIdWithCompany" data-automation-id="form_register_global_id_with_company">
                    <Box id="gid_registration_intro" data-automation-id="gid_registration_intro">{getLabelFromJspVariables('globalid_login_single')}</Box>
                    <TextInput
                        label={getLabelFromJspVariables('label_company_name')}
                        id='company_desc_input'
                        dataAutomationId='company_desc_input'
                        value={companyDesc}
                        disabled={true}
                    />
                    <TextInput
                        label={getLabelFromJspVariables('label_email_address')}
                        id='email_input'
                        dataAutomationId='email_input'
                        value={email}
                        required
                        placeholder={getLabelFromJspVariables('globalid_email_is_login')}
                        error={!!errors.email}
                        helperText={errors.email || ''}
                        onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
                    />
                    <TextInput
                        label={getLabelFromJspVariables('label_first_name')}
                        id='first_name_input'
                        dataAutomationId='first_name_input'
                        value={firstName}
                        required
                        error={!!errors.firstName}
                        helperText={errors.firstName || ''}
                        onChange={e => { setFirstName(e.target.value); clearFieldError('firstName'); }}
                    />
                    <TextInput
                        label={getLabelFromJspVariables('label_last_name')}
                        id='last_name_input'
                        dataAutomationId='last_name_input'
                        value={lastName}
                        required
                        error={!!errors.lastName}
                        helperText={errors.lastName || ''}
                        onChange={e => { setLastName(e.target.value); clearFieldError('lastName'); }}
                    />
                    {!ignoreCaptcha && <Box sx={{ pt: 1 }}>
                        <ReCAPTCHA sitekey={constants.GOOGLE_RECAPTCHA_V2_SITE_KEY}
                            onChange={val => setCaptchaResponse(val)}
                        />
                    </Box>}
                    <Box id="tfa-info-box" data-automation-id="tfa_info_box" className="help-notes" sx={{ mt: 1 }}>
                        <Box>
                            {getLabelFromJspVariables('title_tfa_registration')}
                        </Box>
                        <Box>
                            {getLabelFromJspVariables('tfa_new_reg')}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" fullWidth type="submit"
                            data-automation-id="register_global_id_company_send_confirmation_btn">
                            {getLabelFromJspVariables('send_confirmation_email')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageRegisterGlobalIdWithCompany;
